import React, { useState, useEffect, useMemo } from 'react';
import { useEditor } from '../store/EditorContext';
import { InputControl, ModifierType, ConditionOperator, Field, Group } from '../types';
import toast from 'react-hot-toast';

export const PreviewModal: React.FC = () => {
    const { state } = useEditor();
    const [formData, setFormData] = useState<Record<string, any>>({});
    
    // Group visibility state (for collapsible)
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    useEffect(() => {
        // Initialize default collapsed state
        const initial: Record<string, boolean> = {};
        state.form.groups.forEach(g => {
            if (g.collapsible && g.defaultCollapsed) {
                initial[g.id] = true;
            }
        });
        setCollapsedGroups(initial);
    }, [state.form.groups]);

    const handleChange = (id: string, value: any) => {
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const toggleGroup = (id: string) => {
        setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Final validation check before submit
        const errors = state.form.fields
            .filter(f => isFieldVisible(f))
            .filter(f => isFieldRequired(f) && !formData[f.id])
            .map(f => f.label);

        if (errors.length > 0) {
            toast.error(`Missing required fields: ${errors.join(', ')}`);
            return;
        }

        console.log('Form Data:', formData);
        toast.success('Form submitted! Check console for data.');
    };

    // Logic Engine
    const checkCondition = (sourceValue: any, operator: ConditionOperator, targetValue: any): boolean => {
        const strSource = String(sourceValue || '').toLowerCase();
        const strTarget = String(targetValue || '').toLowerCase();

        switch (operator) {
            case ConditionOperator.EQUALS: return strSource === strTarget;
            case ConditionOperator.NOT_EQUALS: return strSource !== strTarget;
            case ConditionOperator.CONTAINS: return strSource.includes(strTarget);
            case ConditionOperator.GREATER_THAN: return parseFloat(sourceValue) > parseFloat(targetValue);
            case ConditionOperator.LESS_THAN: return parseFloat(sourceValue) < parseFloat(targetValue);
            case ConditionOperator.IS_TRUE: return sourceValue === true || sourceValue === 'true';
            case ConditionOperator.IS_FALSE: return sourceValue === false || sourceValue === 'false';
            case ConditionOperator.IS_EMPTY: return !sourceValue || sourceValue === '';
            case ConditionOperator.IS_NOT_EMPTY: return !!sourceValue && sourceValue !== '';
            default: return false;
        }
    };

    const evaluateModifier = (field: Field, type: ModifierType): boolean => {
        if (!field.modifiers) return false;

        const relevantModifiers = field.modifiers.filter(m => m.type === type);
        if (relevantModifiers.length === 0) return false;

        // Check each modifier of this type
        for (const mod of relevantModifiers) {
            if (mod.conditions.length === 0) continue;

            const results = mod.conditions.map(cond => {
                const sourceValue = formData[cond.sourceFieldId];
                return checkCondition(sourceValue, cond.operator, cond.value);
            });

            const isMatch = mod.matchType === 'any' 
                ? results.some(r => r) 
                : results.every(r => r);

            if (isMatch) return true;
        }

        return false;
    };

    const isFieldVisible = (field: Field) => !evaluateModifier(field, ModifierType.HIDDEN);
    const isFieldDisabled = (field: Field) => evaluateModifier(field, ModifierType.DISABLED);
    const isFieldRequired = (field: Field) => field.validation?.required || evaluateModifier(field, ModifierType.REQUIRED);

    const renderInput = (field: Field) => {
        const commonClasses = "w-full p-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 placeholder-slate-400 transition-all disabled:bg-slate-50 disabled:text-slate-400 text-slate-900";
        const isDisabled = isFieldDisabled(field);

        switch (field.inputControl) {
            case InputControl.TEXTAREA:
                return (
                    <textarea 
                        className={commonClasses}
                        rows={4}
                        placeholder={field.placeholder || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        disabled={isDisabled}
                    />
                );
            case InputControl.SELECT:
                return (
                    <select 
                        className={commonClasses} 
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        disabled={isDisabled}
                    >
                        <option value="">Select an option...</option>
                        {field.dataSource?.map((opt: any) => (
                            <option key={opt.key} value={opt.key}>{opt.value}</option>
                        ))}
                    </select>
                );
            case InputControl.CHECKBOX:
                return (
                    <input 
                        type="checkbox"
                        className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500 border-slate-300"
                        onChange={(e) => handleChange(field.id, e.target.checked)}
                        disabled={isDisabled}
                    />
                );
            case InputControl.RADIO:
                 return (
                    <div className="space-y-2">
                        {field.dataSource?.map((opt: any) => (
                            <label key={opt.key} className="flex items-center space-x-2">
                                <input 
                                    type="radio"
                                    name={field.id}
                                    value={opt.key}
                                    onChange={(e) => handleChange(field.id, e.target.value)}
                                    className="text-teal-600 focus:ring-teal-500 border-slate-300"
                                    disabled={isDisabled}
                                />
                                <span className={isDisabled ? "text-slate-400" : "text-slate-700"}>{opt.value}</span>
                            </label>
                        ))}
                    </div>
                 );
            default:
                return (
                    <input 
                        type={field.inputControl === InputControl.NUMBER ? 'number' : 'text'}
                        className={commonClasses}
                        placeholder={field.placeholder || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        disabled={isDisabled}
                    />
                );
        }
    };

    const renderField = (field: Field) => {
         if (!isFieldVisible(field)) return null;

         return (
            <div key={field.id} className={field.inputControl === InputControl.CHECKBOX ? "flex items-center gap-3" : ""}>
                <label className={`block font-bold text-slate-700 text-sm ${field.inputControl !== InputControl.CHECKBOX ? "mb-1.5" : ""}`}>
                    {field.label}
                    {isFieldRequired(field) && <span className="text-red-600 ml-1">*</span>}
                </label>
                {renderInput(field)}
            </div>
         );
    };

    // Organize fields
    const groupedFields = useMemo(() => {
        const map: Record<string, Field[]> = {};
        state.form.groups.forEach(g => map[g.id] = []);
        map['root'] = [];
        state.form.fields.forEach(f => {
            const key = f.groupId && map[f.groupId] ? f.groupId : 'root';
            map[key].push(f);
        });
        return map;
    }, [state.form.fields, state.form.groups]);

    return (
        <div className="flex-1 bg-slate-100 p-8 overflow-y-auto flex justify-center animate-in zoom-in-95 duration-200">
            <div className="w-full max-w-6xl">
                <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 md:p-12">
                    <div className="mb-10 text-center border-b border-slate-100 pb-6">
                        <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{state.form.name}</h2>
                        <p className="text-slate-500 mt-2 font-mono text-sm">v{state.form.version}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Render Groups First */}
                        {state.form.groups.map(group => {
                            const fields = groupedFields[group.id];
                            if (fields.length === 0) return null;
                            const isCollapsed = collapsedGroups[group.id];

                            return (
                                <fieldset key={group.id} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                    <legend className="sr-only">{group.label}</legend>
                                    <div 
                                        className={`bg-slate-50 p-4 border-b border-slate-100 flex items-center justify-between ${group.collapsible ? 'cursor-pointer hover:bg-slate-100 transition-colors' : ''}`}
                                        onClick={() => group.collapsible && toggleGroup(group.id)}
                                    >
                                        <h3 className="font-bold text-lg text-slate-800">{group.label}</h3>
                                        {group.collapsible && (
                                            <span className="text-slate-400">
                                                {isCollapsed ? '▼' : '▲'}
                                            </span>
                                        )}
                                    </div>
                                    {!isCollapsed && (
                                        <div className="p-6 space-y-6 bg-white">
                                            {fields.map(renderField)}
                                        </div>
                                    )}
                                </fieldset>
                            );
                        })}

                        {/* Render Ungrouped Fields */}
                        {groupedFields['root'].length > 0 && (
                            <div className="space-y-6">
                                {groupedFields['root'].map(renderField)}
                            </div>
                        )}

                        <div className="pt-8 border-t border-slate-100">
                            <button 
                                type="submit"
                                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg shadow-teal-600/20"
                            >
                                Submit Application
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};